import { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { MagnifyingGlass, CircleNotch, DownloadSimple, Eye, FolderOpen, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";
import api from "../lib/api";
import { FilePreview, previewableExt } from "./FilePreview";

const fmtSize = (b) => {
  if (!b) return "0 KB";
  if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
};

export const DossierSearch = () => {
  const { t, lang } = useLang();
  const d = t.dossier;
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reporting, setReporting] = useState(false);

  const downloadReportAll = async () => {
    setReporting(true);
    try {
      const res = await api.get(`/dossier/report-all?lang=${lang}&t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "report-fascicoli-completo.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success(d.reportStarted);
    } catch (e) {
      toast.error(e?.response?.status === 400 ? d.noResults : d.downloadError);
    } finally { setReporting(false); }
  };

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); return; }
    setBusy(true);
    const h = setTimeout(() => {
      api.get(`/dossier/search?q=${encodeURIComponent(term)}`)
        .then(({ data }) => setResults(data.results))
        .catch(() => setResults([]))
        .finally(() => setBusy(false));
    }, 400);
    return () => { clearTimeout(h); setBusy(false); };
  }, [q]);

  const download = async (f) => {
    try {
      const res = await api.get(`/machines/${f.machine_id}/dossier/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(d.downloadError); }
  };

  return (
    <div className="mb-8 rounded-md border border-slate-200 bg-white p-5" data-testid="dossier-global-search">
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono-tech text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <FolderOpen size={14} className="text-emerald-600" /> {d.globalTitle}
        </p>
        <button onClick={downloadReportAll} disabled={reporting}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-700 transition-colors disabled:opacity-60"
          data-testid="dossier-report-all-btn">
          {reporting ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} />} {d.reportAllBtn}
        </button>
      </div>
      <div className="relative mt-3 max-w-xl">
        <MagnifyingGlass size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={d.globalPlaceholder}
          className="w-full rounded-full border border-slate-300 bg-white pl-11 pr-10 py-2.5 text-sm text-[#0F172A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition"
          data-testid="dossier-global-input" />
        {busy && <CircleNotch size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-500 animate-spin" />}
      </div>
      {results !== null && (
        results.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500" data-testid="dossier-global-empty">{d.noResults}</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100" data-testid="dossier-global-results">
            {results.map((f, i) => (
              <li key={f.id} className="flex items-center gap-2 py-2.5" data-testid={`dossier-global-result-${i}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#0F172A] truncate">{f.file_name}</p>
                  <p className="text-[11px] text-slate-400 font-mono-tech truncate">
                    {f.machine_name}{f.machine_serial ? ` · ${f.machine_serial}` : ""} · {fmtSize(f.size)}
                    {f.created_at ? ` · ${new Date(f.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}` : ""}
                  </p>
                  {f.note && <p className="text-[11px] text-slate-500 italic truncate">{f.note}</p>}
                </div>
                {previewableExt(f.file_name) && (
                  <button onClick={() => setPreview(f)} className="text-slate-400 hover:text-emerald-600 transition-colors p-1.5" title={d.preview} data-testid={`dossier-global-preview-${i}`}><Eye size={17} /></button>
                )}
                <button onClick={() => download(f)} className="text-slate-400 hover:text-emerald-600 transition-colors p-1.5" title={d.download} data-testid={`dossier-global-download-${i}`}><DownloadSimple size={17} /></button>
              </li>
            ))}
          </ul>
        )
      )}
      <AnimatePresence>
        {preview && <FilePreview file={preview} machineId={preview.machine_id} onClose={() => setPreview(null)} />}
      </AnimatePresence>
    </div>
  );
};
